export type StreetSurfaceFamily = "old_town" | "general";

export interface StreetSurfaceFamilyConfig {
  readonly carriagewayLiftM: number;
  readonly curbHeightM: number;
  readonly curbWidthM: number;
  readonly sidewalkWidthM: number;
  readonly pedestrianLiftM: number;
}

export interface NamedStreetSurfaceProfile {
  readonly sidewalkWidthM: number;
}

export interface PedestrianSourceSurfaceProfile {
  readonly widthM: number;
  readonly surfaceClass?: "cycleway";
}

export interface StreetSurfaceConfig {
  readonly maximumSegmentLengthM: number;
  /** Length of the eased apron where a road becomes a pedestrian street. */
  readonly pedestrianTransitionLengthM: number;
  /** Depth of the rounded gateway cap at the pedestrian threshold. */
  readonly pedestrianGatewayCurveDepthM: number;
  /** Road-centre depth keeps the asphalt end broad instead of pointed. */
  readonly pedestrianGatewayCentreDepthM: number;
  readonly pedestrianGatewayCurveSegments: number;
  /** Extra tessellation keeps the curb-height ramp visibly smooth. */
  readonly pedestrianTransitionMaximumSegmentLengthM: number;
  /** Mirrors the source-side endpoint match used to infer public-realm width. */
  readonly pedestrianTransitionEndpointToleranceM: number;
  readonly junctionEndpointToleranceM: number;
  readonly junctionCornerClearanceM: number;
  readonly junctionAcuteCornerMaximumCutbackM: number;
  readonly junctionCornerSegments: number;
  readonly junctionSurfaceLiftM: number;
  readonly defaultCarriagewayWidthM: number;
  readonly wideCarriagewayWidthM: number;
  readonly pedestrianWidthM: number;
  readonly cyclewayWidthM: number;
  readonly families: Readonly<
    Record<StreetSurfaceFamily, StreetSurfaceFamilyConfig>
  >;
  readonly namedStreetProfiles: Readonly<
    Record<string, Readonly<NamedStreetSurfaceProfile>>
  >;
  readonly pedestrianSourceProfiles: Readonly<
    Record<string, Readonly<PedestrianSourceSurfaceProfile>>
  >;
}

export interface AuthoredStreetGradeProfile {
  readonly id: string;
  readonly sourceFeatureIds: ReadonlySet<string>;
  /** Elevated roads get a slab shell; subgrade roads remain below terrain. */
  readonly structure?: "elevated" | "subgrade";
  /** World-space bank anchors sampled from the checked-in terrain fixture. */
  readonly start: Readonly<{ x: number; z: number; elevationM: number }>;
  readonly end: Readonly<{ x: number; z: number; elevationM: number }>;
  /** Optional curved/vertical alignment; nearest-segment interpolation wins. */
  readonly anchors?: readonly Readonly<{
    x: number;
    z: number;
    elevationM: number;
  }>[];
  /** When false, source fragments return to terrain beyond the anchor run. */
  readonly clampOutsideRange?: boolean;
  /** Visual/physical slab thickness; user-facing and intentionally tunable. */
  readonly deckDepthM: number;
  /** Below this terrain clearance the road remains part of ground topology. */
  readonly elevatedClearanceThresholdM: number;
}

export interface AuthoredStreetEarthworkAnchor {
  readonly x: number;
  readonly z: number;
  /** Vertical cut below the checked-in terrain at this centreline anchor. */
  readonly cutDepthM: number;
}

export interface AuthoredStreetEarthworkProfile {
  readonly id: string;
  /** Ordered world-space centreline anchors through the cut. */
  readonly anchors: readonly Readonly<AuthoredStreetEarthworkAnchor>[];
  /** Full-depth road platform measured from the centreline. */
  readonly coreHalfWidthM: number;
  /** Additional smooth terrain transition outside the road platform. */
  readonly bankWidthM: number;
}

export interface AuthoredPedestrianStreetWidthAnchor {
  readonly x: number;
  readonly z: number;
  readonly widthM: number;
}

export interface AuthoredPedestrianStreetWidthProfile {
  readonly id: string;
  readonly sourceFeatureId: string;
  readonly anchors: readonly Readonly<AuthoredPedestrianStreetWidthAnchor>[];
}

/**
 * GRPK carries the complete 14 m pedestrian public-realm envelope along this
 * Aušros Vartų g. source feature. At the Gate of Dawn that public realm
 * contracts into OSM way 8020532, an explicitly mapped building passage fed
 * by a 3.4 m paving-stone living street. These world-space anchors preserve
 * the official centreline while narrowing only the final gate approach.
 */
export const GATE_OF_DAWN_STREET_WIDTH_PROFILE:
  Readonly<AuthoredPedestrianStreetWidthProfile> = Object.freeze({
    id: "gate-of-dawn-building-passage",
    sourceFeatureId: "grpk:72797571-7d04-4618-a103-84ad237675eb",
    anchors: Object.freeze([
      Object.freeze({ x: 766.305855, z: 1536, widthM: 14 }),
      Object.freeze({ x: 768, z: 1544.149122, widthM: 3.4 }),
      Object.freeze({ x: 772.771, z: 1585.009, widthM: 3.4 }),
    ]),
  });

export const AUTHORED_PEDESTRIAN_STREET_WIDTH_PROFILES = Object.freeze([
  GATE_OF_DAWN_STREET_WIDTH_PROFILE,
]);

/**
 * J. Jasinskio crosses Geležinio Vilko on a viaduct. GRPK supplies the real
 * centreline and widths but no vertical/layer attribute, so the deck grade is
 * the straight bank-to-bank chord between its checked-in terrain elevations.
 * That preserves the source alignment, meets the terrain at both approaches,
 * and lifts only the four source fragments spanning the verified crossing.
 */
export const JASINSKIO_GELEZINIO_VILKO_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "jasinskio-gelezinio-vilko-viaduct",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:fb366e3a-fcee-454a-84e0-ddb539b21380",
      "grpk:cd45bcd4-00d4-4c44-98f8-c2ef35bf7e50",
      "grpk:1dd83d7a-d578-4435-aae5-19a707ff27a7",
      "grpk:aaa59249-ae71-498c-9524-4ac019027b6f",
    ])),
    start: Object.freeze({
      x: -1_191.086,
      z: -9.366,
      elevationM: 14.77669,
    }),
    end: Object.freeze({
      x: -931.507,
      z: 37.877,
      elevationM: 16.041012,
    }),
    deckDepthM: 0.72,
    elevatedClearanceThresholdM: 0.45,
  });

/**
 * Geležinio Vilko runs below Gedimino pr. in the covered tunnel beneath
 * Nepriklausomybės aikštė. GRPK joins the two carriageways to the avenue in
 * plan but carries no vertical/layer attribute, which otherwise produces two
 * false at-grade junctions. The checked-in terrain follows the lower road at
 * the crossing, so this bank-to-bank avenue chord restores the upper surface
 * without moving either official centreline.
 *
 * The endpoints and elevations are sampled from the checked-in terrain where
 * Gedimino pr. returns to ground level. See the authored provenance record in
 * data/overrides/roads/gedimino-gelezinio-grade-separation.json.
 */
export const GEDIMINO_GELEZINIO_VILKO_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "gedimino-gelezinio-vilko-tunnel-deck",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:529563be-6c02-4b63-b052-70e7aa544977",
      "grpk:10bd0fc3-dfa7-41f6-9cb2-219ff1932489",
      "grpk:1d5e2009-fb20-4e6b-a4cf-526b93226dbb",
      "grpk:57b2eb92-e68b-425f-979b-086c3dd18b90",
    ])),
    start: Object.freeze({
      x: -1_171.62,
      z: -204.685,
      elevationM: 15.733588,
    }),
    end: Object.freeze({
      x: -787.645,
      z: -104.164,
      elevationM: 17.377086,
    }),
    deckDepthM: 0.72,
    elevatedClearanceThresholdM: 0.45,
  });

/**
 * Saltoniškių žiedas crosses the northbound/southbound Geležinio Vilko
 * carriageways on two short bridge decks. OSM ways 334067380 and 334067866
 * explicitly carry bridge=yes and layer=1; the municipality describes
 * Geležinio Vilko as passing below the ring. The checked-in 16 m terrain loses
 * that underpass, so the ring decks retain their checked-in terrain elevation
 * while one shared earthwork profile restores the lower road corridor.
 *
 * See data/overrides/roads/saltoniskes-grade-separation.json for provenance,
 * source-to-GRPK matching, and the clearance derivation.
 */
export const SALTONISKES_NORTH_BRIDGE_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "saltoniskes-ring-north-bridge",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:d7ef7b3f-4ac3-4448-a153-10872cf284fa",
      "grpk:6b61a5a8-edbc-46c7-b388-f005d45c2b79",
      "grpk:7fa5e4b1-cae2-4646-934d-907f735f51b1",
      "grpk:c7172a3c-cace-4772-b0ac-f03904b88f8f",
    ])),
    start: Object.freeze({
      x: -995.099,
      z: -1_053.26,
      elevationM: 20.026456,
    }),
    end: Object.freeze({
      x: -913.979,
      z: -1_044.697,
      elevationM: 19.854281,
    }),
    deckDepthM: 0.72,
    elevatedClearanceThresholdM: 0.45,
  });

export const SALTONISKES_SOUTH_BRIDGE_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "saltoniskes-ring-south-bridge",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:634f9a22-fbba-4726-bc6d-694b11200359",
      "grpk:5805a40e-c40b-4098-b739-90efe50a856b",
      "grpk:e8845577-1b0e-4dea-8c90-dbcb125eafd2",
    ])),
    start: Object.freeze({
      x: -981.475,
      z: -1_168.666,
      elevationM: 21.194313,
    }),
    end: Object.freeze({
      x: -928.937,
      z: -1_173.687,
      elevationM: 21.149963,
    }),
    deckDepthM: 0.72,
    elevatedClearanceThresholdM: 0.45,
  });

/** Olandų ring crosses the curving Stepono Batoro / Drujos corridor twice. */
export const OLANDU_EAST_RING_BRIDGE_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "olandu-east-ring-bridge",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:d8cc2f9e-9fb7-47e5-b5fe-0f82015ba69f",
      "grpk:c2cc45fa-63c5-43ef-95c4-fffe47ecac58",
    ])),
    start: Object.freeze({ x: 2_389.776, z: 694.351, elevationM: 41.876903 }),
    end: Object.freeze({ x: 2_381.356, z: 643.549, elevationM: 42.824195 }),
    deckDepthM: 0.72,
    elevatedClearanceThresholdM: 0.45,
  });

export const OLANDU_SOUTH_RING_BRIDGE_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "olandu-south-ring-bridge",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:a51cc36f-877c-4270-be8e-63a7eb20253f",
      "grpk:02000d11-e8ec-480c-90e8-520436c786c9",
      "grpk:fb3559cb-d7e4-4a95-b05e-5bb55502295a",
    ])),
    start: Object.freeze({ x: 2_301.635, z: 753.041, elevationM: 36.067899 }),
    end: Object.freeze({ x: 2_359.466, z: 754.38, elevationM: 38.099826 }),
    deckDepthM: 0.72,
    elevatedClearanceThresholdM: 0.45,
  });

/** Northern Savanorių ring arc over the open Geležinio Vilko cutting. */
export const SAVANORIU_NORTH_RING_BRIDGE_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "savanoriu-north-ring-bridge",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:e95b20fd-b0ca-44f0-81d0-43ae486c5092",
      "grpk:04e1a5ef-4e07-4262-964d-d764d40f9d7f",
    ])),
    start: Object.freeze({ x: -2_525.493, z: 2_123.754, elevationM: 30.944096 }),
    end: Object.freeze({ x: -2_572.875, z: 2_142.125, elevationM: 29.231654 }),
    deckDepthM: 0.82,
    elevatedClearanceThresholdM: 0.45,
  });

/** Ground-level southern ring arc forming the roof of the paired A1 tunnel. */
export const SAVANORIU_SOUTH_RING_TUNNEL_DECK_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "savanoriu-south-ring-tunnel-deck",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:82b32e4b-4c4a-495b-be09-abf0979fe31f",
      "grpk:97b94642-585f-493f-9c70-620cb5f89050",
      "grpk:d3e92d7b-fdf2-426f-9036-718a11ebaf7d",
    ])),
    start: Object.freeze({ x: -2_572.731, z: 2_296.382, elevationM: 28.071483 }),
    end: Object.freeze({ x: -2_525.199, z: 2_312.563, elevationM: 29.682062 }),
    deckDepthM: 0.82,
    elevatedClearanceThresholdM: 0.45,
  });

/**
 * OSM maps the long paired Tūkstantmečio carriageways as bridge=yes/layer=1.
 * GRPK stores one 20-26 m public-road centreline whose name changes through
 * the interchange; the complete official chain therefore shares one grade.
 */
export const SAVANORIU_TUKSTANTMECIO_FLYOVER_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "savanoriu-tukstantmecio-flyover",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:d47b3b9a-7bdd-400e-93f8-4787572400df",
      "grpk:b14dc7a2-fcf9-4625-b1d8-af4028d31bb7",
      "grpk:bdb0af9b-bf82-484c-b8f1-ce8dca30229c",
      "grpk:88723b79-c855-487c-bc8a-1d00241f7e9c",
      "grpk:ca66e4cf-43a1-4cc8-b16f-8651cda15793",
      "grpk:a8df8855-fef4-40fc-88e7-31eaaeb55bf9",
    ])),
    start: Object.freeze({ x: -2_054.824, z: 2_417.504, elevationM: 57.906315 }),
    end: Object.freeze({ x: -2_955.123, z: 2_099.34, elevationM: 16.14551 }),
    deckDepthM: 0.9,
    elevatedClearanceThresholdM: 0.45,
  });

const SAVANORIU_EAST_TUNNEL_ANCHORS = Object.freeze([
  Object.freeze({ x: -2_533.567, z: 2_247.485, elevationM: 29.454308 }),
  Object.freeze({ x: -2_539.008, z: 2_288.008, elevationM: 23.959445 }),
  Object.freeze({ x: -2_543.888, z: 2_309.968, elevationM: 23.249775 }),
  Object.freeze({ x: -2_550.083, z: 2_331.61, elevationM: 25.806297 }),
  Object.freeze({ x: -2_560, z: 2_369.659, elevationM: 28.323564 }),
]);

export const SAVANORIU_EAST_TUNNEL_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "savanoriu-east-tunnel-carriageway",
    structure: "subgrade",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:a471b283-2822-4767-889c-3190d836bb8a",
      "grpk:36fcb06c-08d3-4295-9ad8-9b1a5f1c7d0a",
    ])),
    start: SAVANORIU_EAST_TUNNEL_ANCHORS[0]!,
    end: SAVANORIU_EAST_TUNNEL_ANCHORS.at(-1)!,
    anchors: SAVANORIU_EAST_TUNNEL_ANCHORS,
    clampOutsideRange: false,
    deckDepthM: 0,
    elevatedClearanceThresholdM: 0.45,
  });

const SAVANORIU_WEST_TUNNEL_ANCHORS = Object.freeze([
  Object.freeze({ x: -2_545.714, z: 2_243.317, elevationM: 29.124051 }),
  Object.freeze({ x: -2_551.559, z: 2_288.345, elevationM: 23.549942 }),
  Object.freeze({ x: -2_556.109, z: 2_305.601, elevationM: 22.803063 }),
  Object.freeze({ x: -2_563.171, z: 2_330.262, elevationM: 25.263003 }),
  Object.freeze({ x: -2_576.452, z: 2_386.657, elevationM: 27.663459 }),
]);

export const SAVANORIU_WEST_TUNNEL_GRADE_PROFILE:
  Readonly<AuthoredStreetGradeProfile> = Object.freeze({
    id: "savanoriu-west-tunnel-carriageway",
    structure: "subgrade",
    sourceFeatureIds: Object.freeze(new Set([
      "grpk:2bf19f96-d36d-405e-886b-5a7c8989d0f8",
      "grpk:e8ec853f-6c49-4852-833d-a0a9478867fb",
    ])),
    start: SAVANORIU_WEST_TUNNEL_ANCHORS[0]!,
    end: SAVANORIU_WEST_TUNNEL_ANCHORS.at(-1)!,
    anchors: SAVANORIU_WEST_TUNNEL_ANCHORS,
    clampOutsideRange: false,
    deckDepthM: 0,
    elevatedClearanceThresholdM: 0.45,
  });

export const AUTHORED_STREET_GRADE_PROFILES = Object.freeze([
  JASINSKIO_GELEZINIO_VILKO_GRADE_PROFILE,
  GEDIMINO_GELEZINIO_VILKO_GRADE_PROFILE,
  SALTONISKES_NORTH_BRIDGE_GRADE_PROFILE,
  SALTONISKES_SOUTH_BRIDGE_GRADE_PROFILE,
  OLANDU_EAST_RING_BRIDGE_GRADE_PROFILE,
  OLANDU_SOUTH_RING_BRIDGE_GRADE_PROFILE,
  SAVANORIU_NORTH_RING_BRIDGE_GRADE_PROFILE,
  SAVANORIU_SOUTH_RING_TUNNEL_DECK_GRADE_PROFILE,
  SAVANORIU_TUKSTANTMECIO_FLYOVER_GRADE_PROFILE,
  SAVANORIU_EAST_TUNNEL_GRADE_PROFILE,
  SAVANORIU_WEST_TUNNEL_GRADE_PROFILE,
]);

export const SALTONISKES_GELEZINIO_VILKO_EARTHWORK_PROFILE:
  Readonly<AuthoredStreetEarthworkProfile> = Object.freeze({
    id: "saltoniskes-gelezinio-vilko-underpass",
    anchors: Object.freeze([
      Object.freeze({ x: -982.7, z: -1_400, cutDepthM: 0 }),
      Object.freeze({ x: -968.4, z: -1_280, cutDepthM: 3.12 }),
      Object.freeze({ x: -957.1, z: -1_172, cutDepthM: 5.72 }),
      Object.freeze({ x: -952, z: -1_100, cutDepthM: 5.72 }),
      Object.freeze({ x: -940.1, z: -1_040, cutDepthM: 5.72 }),
      Object.freeze({ x: -931.5, z: -960, cutDepthM: 3.5 }),
      Object.freeze({ x: -928.2, z: -880, cutDepthM: 0 }),
    ]),
    coreHalfWidthM: 14,
    bankWidthM: 14,
  });

export const OLANDU_STEPONO_BATORO_EARTHWORK_PROFILE:
  Readonly<AuthoredStreetEarthworkProfile> = Object.freeze({
    id: "olandu-stepono-batoro-underpass",
    anchors: Object.freeze([
      Object.freeze({ x: 2_496.45, z: 598.812, cutDepthM: 0 }),
      Object.freeze({ x: 2_431.06, z: 638.8, cutDepthM: 3.2 }),
      Object.freeze({ x: 2_389.76, z: 669.431, cutDepthM: 5.72 }),
      Object.freeze({ x: 2_349.255, z: 711.121, cutDepthM: 5.72 }),
      Object.freeze({ x: 2_316.762, z: 763.681, cutDepthM: 5.72 }),
      Object.freeze({ x: 2_289.845, z: 824, cutDepthM: 3.2 }),
      Object.freeze({ x: 2_242.855, z: 913.975, cutDepthM: 0 }),
    ]),
    // The checked-in terrain grid is 16 m; ten metres guarantees at least one
    // full-depth sample across the eight-metre lower carriageway.
    coreHalfWidthM: 10,
    bankWidthM: 12,
  });

export const SAVANORIU_NORTH_UNDERPASS_EARTHWORK_PROFILE:
  Readonly<AuthoredStreetEarthworkProfile> = Object.freeze({
    id: "savanoriu-north-underpass",
    anchors: Object.freeze([
      Object.freeze({ x: -2_541.674, z: 1_985.378, cutDepthM: 0 }),
      Object.freeze({ x: -2_554.787, z: 2_066.742, cutDepthM: 3.5 }),
      Object.freeze({ x: -2_550.784, z: 2_129.934, cutDepthM: 5.72 }),
      Object.freeze({ x: -2_545.018, z: 2_178.53, cutDepthM: 3.5 }),
      Object.freeze({ x: -2_540.161, z: 2_219.786, cutDepthM: 0 }),
    ]),
    coreHalfWidthM: 10,
    bankWidthM: 14,
  });

/**
 * Continuous cutting for the paired A1/A4/A16 tunnel carriageways. The cut
 * must remain at full depth beneath the southern ring deck: ending the
 * earthwork at either portal leaves a terrain wall across the sourced road.
 */
export const SAVANORIU_TUNNEL_EARTHWORK_PROFILE:
  Readonly<AuthoredStreetEarthworkProfile> = Object.freeze({
    id: "savanoriu-tunnel-carriageways",
    anchors: Object.freeze([
      Object.freeze({ x: -2_539.64, z: 2_245.4, cutDepthM: 0 }),
      Object.freeze({ x: -2_545.284, z: 2_288.176, cutDepthM: 5.2 }),
      Object.freeze({ x: -2_547.5, z: 2_292.86, cutDepthM: 5.72 }),
      Object.freeze({ x: -2_555, z: 2_320, cutDepthM: 5.72 }),
      Object.freeze({ x: -2_557, z: 2_331, cutDepthM: 3.2 }),
      Object.freeze({ x: -2_572, z: 2_380, cutDepthM: 0 }),
    ]),
    coreHalfWidthM: 14,
    bankWidthM: 12,
  });

export const AUTHORED_STREET_EARTHWORK_PROFILES = Object.freeze([
  SALTONISKES_GELEZINIO_VILKO_EARTHWORK_PROFILE,
  OLANDU_STEPONO_BATORO_EARTHWORK_PROFILE,
  SAVANORIU_NORTH_UNDERPASS_EARTHWORK_PROFILE,
  SAVANORIU_TUNNEL_EARTHWORK_PROFILE,
]);

/**
 * Official GRPK splits the Geležinio Vilko / J. Jasinskio interchange into
 * separate carriageways and ramp fragments. These source lines have no matched
 * sidewalk evidence; applying the ordinary two-sided fallback to every
 * fragment creates curb islands through the carriageway and median. Explicit
 * source sidewalk modes still take precedence over this omission list.
 */
export const NO_PROCEDURAL_SIDEWALK_FALLBACK_SOURCE_IDS = Object.freeze(
  new Set([
    "grpk:012595cd-875e-42a1-aee3-0ee0fb21c04e",
    "grpk:29ab45ee-9691-4e2e-84fe-f3520c76f6d3",
    "grpk:3c698244-a106-4f0e-a4b1-3a09008c2cb5",
    "grpk:4c722326-42ea-452e-80db-e9f974f8176f",
    "grpk:528914a9-3b52-4f81-aa3f-3eaf1772937d",
    "grpk:5ca13567-4542-444d-83cb-30b4e74c9fa8",
    "grpk:66933616-0732-4db1-bf80-51efe02e935f",
    "grpk:6d6d28e8-6d09-4be8-8a9b-d2c4c1fae651",
    "grpk:86853e2b-1525-4e0a-8cff-c6f5db74d7cf",
    "grpk:87d4b705-134c-46a0-9fe5-0bccaf6419b5",
    "grpk:99e66c33-20fd-466b-84b7-629d41edd839",
    "grpk:a24b52e1-6062-43fe-a490-9a53787aec86",
    "grpk:aa022d03-0925-41d2-9fd2-42b2225e363d",
    "grpk:b1d11146-abad-4877-9b7e-0d1f615f8639",
    "grpk:d2ef0bb0-4871-4745-98be-1729fc34cc0b",
    "grpk:eaf8d30d-8069-4212-a8a9-a751152fd0fc",
    "grpk:ed73e393-8e86-428d-a08d-787effe3c7cc",
    "grpk:f69a258e-96e7-4561-8a4f-fd8a2e183688",
    "grpk:fb62566c-1f70-4f02-896c-e15aadf8f4f8",
    // The bridge ways use separately mapped pedestrian routes. Do not place
    // the generic two-sided sidewalk cross-section across the underpass.
    "grpk:d7ef7b3f-4ac3-4448-a153-10872cf284fa",
    "grpk:6b61a5a8-edbc-46c7-b388-f005d45c2b79",
    "grpk:7fa5e4b1-cae2-4646-934d-907f735f51b1",
    "grpk:c7172a3c-cace-4772-b0ac-f03904b88f8f",
    "grpk:634f9a22-fbba-4726-bc6d-694b11200359",
    "grpk:5805a40e-c40b-4098-b739-90efe50a856b",
    "grpk:e8845577-1b0e-4dea-8c90-dbcb125eafd2",
    // Olandų bridge approaches and the Savanorių elevated/subgrade stack use
    // separately mapped pedestrian routes or explicitly omit sidewalks.
    "grpk:d8cc2f9e-9fb7-47e5-b5fe-0f82015ba69f",
    "grpk:c2cc45fa-63c5-43ef-95c4-fffe47ecac58",
    "grpk:a51cc36f-877c-4270-be8e-63a7eb20253f",
    "grpk:02000d11-e8ec-480c-90e8-520436c786c9",
    "grpk:fb3559cb-d7e4-4a95-b05e-5bb55502295a",
    "grpk:e95b20fd-b0ca-44f0-81d0-43ae486c5092",
    "grpk:04e1a5ef-4e07-4262-964d-d764d40f9d7f",
    "grpk:82b32e4b-4c4a-495b-be09-abf0979fe31f",
    "grpk:97b94642-585f-493f-9c70-620cb5f89050",
    "grpk:d3e92d7b-fdf2-426f-9036-718a11ebaf7d",
    "grpk:d47b3b9a-7bdd-400e-93f8-4787572400df",
    "grpk:b14dc7a2-fcf9-4625-b1d8-af4028d31bb7",
    "grpk:bdb0af9b-bf82-484c-b8f1-ce8dca30229c",
    "grpk:88723b79-c855-487c-bc8a-1d00241f7e9c",
    "grpk:ca66e4cf-43a1-4cc8-b16f-8651cda15793",
    "grpk:a8df8855-fef4-40fc-88e7-31eaaeb55bf9",
    "grpk:a471b283-2822-4767-889c-3190d836bb8a",
    "grpk:36fcb06c-08d3-4295-9ad8-9b1a5f1c7d0a",
    "grpk:2bf19f96-d36d-405e-886b-5a7c8989d0f8",
    "grpk:e8ec853f-6c49-4852-833d-a0a9478867fb",
  ]),
);

/**
 * Reference-derived street cross sections. The Old Town family follows the
 * user's dark cobble / stone-edge reference; the general family follows the
 * asphalt / precast-curb / concrete-slab reference.
 */
export const STREET_SURFACE_CONFIG: Readonly<StreetSurfaceConfig> =
  Object.freeze({
    maximumSegmentLengthM: 6,
    // User-facing public-realm dimensions; intentionally easy to retune after
    // walking the transitions in the full city build.
    pedestrianTransitionLengthM: 12,
    pedestrianGatewayCurveDepthM: 4,
    pedestrianGatewayCentreDepthM: 1,
    pedestrianGatewayCurveSegments: 8,
    pedestrianTransitionMaximumSegmentLengthM: 1,
    pedestrianTransitionEndpointToleranceM: 0.25,
    junctionEndpointToleranceM: 0.08,
    junctionCornerClearanceM: 0.9,
    // Acute forks need to stay merged until their real outer envelopes stop
    // overlapping. Bound malformed or nearly parallel source fragments so a
    // single bad angle cannot consume an entire street into one junction.
    junctionAcuteCornerMaximumCutbackM: 64,
    junctionCornerSegments: 4,
    // Junction caps sit above the unbroken base carriageways; lane paint is
    // higher again at 0.018 m, preserving a stable visual stack in flight.
    junctionSurfaceLiftM: 0.012,
    defaultCarriagewayWidthM: 5.6,
    wideCarriagewayWidthM: 8.4,
    pedestrianWidthM: 1.7,
    cyclewayWidthM: 2.5,
    families: Object.freeze({
      old_town: Object.freeze({
        carriagewayLiftM: 0.004,
        curbHeightM: 0.09,
        curbWidthM: 0.28,
        sidewalkWidthM: 1.72,
        pedestrianLiftM: 0.006,
      }),
      general: Object.freeze({
        carriagewayLiftM: 0.004,
        curbHeightM: 0.14,
        curbWidthM: 0.3,
        sidewalkWidthM: 1.9,
        pedestrianLiftM: 0.006,
      }),
    }),
    namedStreetProfiles: Object.freeze({
      // The source centreline does not include installed sidewalk polygons.
      // These widths are authored from the supplied May 2024 street-level
      // reference and keep the broad hardscape promenades instead of filling
      // the building-to-curb space with generic green terrain.
      "gedimino pr.": Object.freeze({ sidewalkWidthM: 5.2 }),
      // Cathedral Square has a ceremonial paved edge along Šventaragio g.;
      // the generic Old Town cross-section leaves an implausible grass seam.
      "šventaragio g.": Object.freeze({ sidewalkWidthM: 3.8 }),
    }),
    pedestrianSourceProfiles: Object.freeze({
      // The designated, segregated Neris active-travel route is independently
      // mapped in OSM way 1160692193. These official GRPK fragments preserve
      // the more accurate centreline where the two sources coincide; a small
      // separable OSM segment fills the gap between them in the wide fixture.
      "grpk:10c187f2-65cf-4836-baa9-b67a9ac8d2aa": Object.freeze({
        widthM: 2.5,
        surfaceClass: "cycleway",
      }),
      "grpk:04bde2d3-b277-4d88-bd88-b9e2c352488d": Object.freeze({
        widthM: 2.5,
        surfaceClass: "cycleway",
      }),
      // Official GRPK pieces forming the continuous lower-bank route through
      // the central Neris corridor. The municipality's reconstructed bank is
      // a broad pedestrian/cycle promenade with ramps, lighting and terraces;
      // the generic 1.7 m path made this important public-space spine read as
      // an incidental footpath. IDs deliberately omit the per-cell clip
      // suffix so all checked-in fragments receive one consistent width.
      "grpk:58c49695-51f2-402f-b4d0-e25bfb6e3d9a": Object.freeze({
        widthM: 3.6,
      }),
      "grpk:f42faa1c-343f-4a0e-8804-4114ebdd8a91": Object.freeze({
        widthM: 3.6,
      }),
      "grpk:2893f38d-5d22-4242-99ea-ea73d87baaad": Object.freeze({
        widthM: 3.6,
      }),
      "grpk:9ac2fb96-b612-4f07-b84f-50df974e52ba": Object.freeze({
        widthM: 3.6,
      }),
      "grpk:a837bc3f-1e78-4373-b00d-0c68d7912288": Object.freeze({
        widthM: 3.6,
      }),
      // Short official connectors keep the walking line joined at cell seams
      // and provide the real ramp-like approaches to the upper street level.
      "grpk:2f837994-7ded-4cff-8dba-b8a5aa767ff9": Object.freeze({
        widthM: 3,
      }),
      "grpk:1358e397-bdb5-4eea-a87e-b0f51bed529d": Object.freeze({
        widthM: 3,
      }),
      "grpk:dbf01700-0dd0-4b33-812d-0eca85a74055": Object.freeze({
        widthM: 3,
      }),
      "grpk:dff6a6bb-2f87-481c-85c2-0dc9e4b14e77": Object.freeze({
        widthM: 3,
      }),
      "grpk:e2ededb4-25ba-4588-ad56-814a763b3892": Object.freeze({
        widthM: 3,
      }),
    }),
  });
